import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentDto } from 'api/common/models/AttachmentDto';
import FilesUploader from 'common/components/filesUploader/FilesUploader';
import BudgetLineAttachmentsService, { BudgetLineAttachmentDto } from 'api/budgetLineAttachments/BudgetLineAttachmentsService';
import Toast from 'common/services/Toast';
import Logger from 'common/services/Logger';
import { LOGGER_LOG_TYPE } from 'Config';
import CustomFile from 'common/models/CustomFile';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { useDispatch } from 'react-redux';
import { updateLine } from 'store/budget/action';
import styles from './AttachmentsTab.module.scss';

interface Props {
    line: BudgetLineDto;
    disabled: boolean;
}

const toAttachmentDto = (a: BudgetLineAttachmentDto): AttachmentDto => ({
    id: a.id,
    key: a.attachmentId,
    attachmentId: a.attachmentId,
    url: a.url,
    name: a.fileName,
    contentType: a.mimeType,
    size: a.size,
    tempFile: null,
});

function AttachmentsTab ({ line, disabled }: Props): JSX.Element | null {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [savedFiles, setSavedFiles] = useState<AttachmentDto[]>([]);
    const [pendingFiles, setPendingFiles] = useState<AttachmentDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const removedIds = new Set(line.attachmentsToRemove ?? []);
    const files = [...savedFiles, ...pendingFiles].filter(a => !a.attachmentId || !removedIds.has(a.attachmentId));

    const loadSavedAttachments = useCallback(async () => {
        if (!line.id) return;
        try {
            const saved = await BudgetLineAttachmentsService.getAttachments(line.id);
            setSavedFiles(saved.map(toAttachmentDto));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t load line attachments', error);
            Toast.error(t('messages.error_load_info'));
        }
    }, [line.id]);

    useEffect(() => {
        void loadSavedAttachments();
    }, [loadSavedAttachments]);

    const onChange = async (updatedFiles: AttachmentDto[]) => {
        const toUpload = updatedFiles.filter(f => !f.id && f.tempFile && !f.remove);
        const toRemove = updatedFiles.filter(f => f.id && f.remove);

        if (toRemove.length > 0) {
            const file = toRemove[0];
            if (file.attachmentId) {
                const currentRemovals = line.attachmentsToRemove ?? [];
                dispatch(updateLine({
                    key: line.key,
                    attachmentsToRemove: [...currentRemovals, file.attachmentId],
                }));
                setSavedFiles(prev => prev.filter(f => f.attachmentId !== file.attachmentId));
                setPendingFiles(prev => prev.filter(f => f.attachmentId !== file.attachmentId));
            }
            return;
        }

        if (toUpload.length === 0) return;

        try {
            setIsLoading(true);

            const customFiles = toUpload.map(f => f.tempFile as CustomFile);
            const uploaded = await BudgetLineAttachmentsService.uploadPending(line.key, customFiles);
            setPendingFiles(prev => [...prev, ...uploaded.map((u, idx) => ({
                ...toAttachmentDto(u),
                url: u.url || toUpload[idx]?.url,
            }))]);

            // Track pending attachment IDs so only these get linked on save
            const newIds = uploaded.map(u => u.attachmentId);
            const currentPendingIds = line.pendingAttachmentIds ?? [];
            dispatch(updateLine({
                key: line.key,
                pendingAttachmentIds: [...currentPendingIds, ...newIds],
            }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t save attachment change', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            setIsLoading(false);
        }
    };

    const onDownload = (file: AttachmentDto) => {
        if (!file.attachmentId) return;
        void BudgetLineAttachmentsService.download(file.attachmentId, file.name);
    };

    return (
        <div className={styles.container}>
            {isLoading && (
                <div className={styles.loadingOverlay}>
                    <LoadingSpinner size={30} />
                </div>
            )}
            <FilesUploader
                files={files}
                type="files"
                disabled={disabled}
                onChange={onChange}
                onDownload={onDownload}
            />
        </div>
    );
}

export default AttachmentsTab;
